import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import api from '../api/axios';
import { filterPhone, isValidEmail } from '../utils/inputValidation';

const PAYS_OPTIONS = [
  { value: 'Sénégal', flag: '🇸🇳', prix: '25 000 FCFA / mois' },
  { value: 'France',  flag: '🇫🇷', prix: '40 € / mois' },
];

const PH = {
  Sénégal: { nomSalon:'Ex : Salon Beauté Dakar', adresse:'Ex : Rue 10, Mermoz, Dakar', prenom:'Ex : Aminata', nom:'Ex : Diallo', email:'aminata.diallo@gmail.com', telephone:'+221 77 000 00 00' },
  France:  { nomSalon:'Ex : Salon Élégance Paris', adresse:'Ex : 12 rue de la Paix, Paris', prenom:'Ex : Sophie', nom:'Ex : Martin', email:'sophie.martin@gmail.com', telephone:'+33 6 00 00 00 00' },
  default: { nomSalon:'Nom de votre salon', adresse:'Adresse du salon', prenom:'Prénom', nom:'Nom', email:'vous@exemple.com', telephone:'Numéro de téléphone' },
};

const CSS = `
  .ss-input { width:100%; background:#f8fafc; border:1.5px solid #f1f5f9; border-radius:14px; padding:12px 14px; font-size:14px; font-weight:600; color:#0f172a; box-sizing:border-box; transition:all 0.2s; outline:none; }
  .ss-input:focus { background:#fff; box-shadow:0 0 0 3px rgba(59,130,246,0.1); border-color:#3b82f6; }
  .ss-input.err { background:#fff1f2; border-color:#fda4af; }
  .ss-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 16px 40px rgba(59,130,246,0.45) !important; }
  .ss-btn:active:not(:disabled) { transform:scale(0.98); }
  .ss-btn:disabled { opacity:0.7; cursor:not-allowed; }
`;

export default function InscriptionPage() {
  const [form, setForm] = useState({
    pays: '', nomSalon: '', adresse: '',
    prenomAdmin: '', nomAdmin: '', emailAdmin: '',
    telephone: '', password: '', confirmPassword: '',
  });
  const [errors,      setErrors]      = useState({});
  const [loading,     setLoading]     = useState(false);
  const [done,        setDone]        = useState(false);
  const [serverError, setServerError] = useState('');

  const paysInfo = PAYS_OPTIONS.find(p => p.value === form.pays);
  const ph       = PH[form.pays] || PH.default;

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: '' }));
    setServerError('');
  };

  const validate = () => {
    const e = {};
    if (!form.pays)                      e.pays            = 'Choisissez un pays.';
    if (!form.nomSalon.trim())           e.nomSalon        = 'Nom du salon requis.';
    if (!form.adresse.trim())            e.adresse         = 'Adresse requise.';
    if (!form.prenomAdmin.trim())        e.prenomAdmin     = 'Prénom requis.';
    if (!form.nomAdmin.trim())           e.nomAdmin        = 'Nom requis.';
    if (!isValidEmail(form.emailAdmin))  e.emailAdmin      = 'Email invalide.';
    if (!form.telephone.trim())          e.telephone       = 'Numéro de téléphone requis.';
    if (form.password.length < 6)        e.password        = 'Minimum 6 caractères.';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Les mots de passe ne correspondent pas.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setServerError('');
    try {
      await api.post('/auth/pre-register', {
        nomSalon:    form.nomSalon.trim(),
        adresse:     form.adresse.trim(),
        pays:        form.pays,
        prenomAdmin: form.prenomAdmin.trim(),
        nomAdmin:    form.nomAdmin.trim(),
        emailAdmin:  form.emailAdmin.trim(),
        telephone:   form.telephone.trim(),
        password:    form.password,
      });
      setDone(true);
    } catch (err) {
      setServerError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Écran de confirmation ───────────────────────────────────── */
  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <style>{CSS}</style>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-[2.5rem] p-10 text-center border border-slate-100 relative"
          style={{ boxShadow: '0 20px 60px rgba(59,130,246,0.08), 0 4px 16px rgba(0,0,0,0.05)' }}
        >
          <div style={{ position:'absolute', top:0, left:'10%', right:'10%', height:3, background:'linear-gradient(90deg, transparent, #3b82f6, #6366f1, transparent)', borderRadius:'0 0 4px 4px' }} />
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-5">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-xl font-black text-slate-900 mb-2">Vérifiez votre email</h1>
          <p className="text-slate-500 text-sm leading-relaxed mb-2">
            Un email de confirmation a été envoyé à <strong className="text-slate-800">{form.emailAdmin}</strong>.
          </p>
          <p className="text-slate-400 text-xs mb-5">Pensez à vérifier votre dossier spam.</p>
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-6">
            <p className="text-blue-700 text-sm font-bold">Vous serez contacté(e) dans moins de 24 heures après confirmation.</p>
          </div>
          <Link to="/login" className="text-blue-600 font-black text-sm hover:underline">Retour à la connexion</Link>
        </motion.div>
      </div>
    );
  }

  /* ── Formulaire principal ───────────────────────────────────── */
  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center p-4 py-10 relative overflow-hidden">
      <style>{CSS}</style>

      {/* Décorations légères */}
      <div style={{ position:'fixed', top:'-80px', right:'-80px', width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 70%)', pointerEvents:'none' }} />
      <div style={{ position:'fixed', bottom:'-60px', left:'-60px', width:240, height:240, borderRadius:'50%', background:'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)', pointerEvents:'none' }} />
      <div style={{ position:'fixed', top:'40%', left:'6%', width:10, height:10, borderRadius:'50%', background:'#bfdbfe', opacity:0.7, pointerEvents:'none' }} />
      <div style={{ position:'fixed', top:'20%', right:'10%', width:7, height:7, borderRadius:'50%', background:'#c7d2fe', opacity:0.6, pointerEvents:'none' }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg bg-white rounded-[2.5rem] border border-slate-100 relative"
        style={{ boxShadow:'0 20px 60px rgba(59,130,246,0.08), 0 4px 16px rgba(0,0,0,0.05)', padding:'36px 32px 32px' }}
      >
        <div style={{ position:'absolute', top:0, left:'10%', right:'10%', height:3, background:'linear-gradient(90deg, transparent, #3b82f6, #6366f1, transparent)', borderRadius:'0 0 4px 4px' }} />

        <div className="text-center mb-6">
          <div className="inline-block p-3 bg-blue-50 rounded-3xl mb-2">
            <h1 className="text-2xl font-black tracking-tight leading-none">
              <span className="text-slate-900">Sama</span>
              <span style={{ background:'linear-gradient(135deg,#2563eb,#4f46e5)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Salon</span>
            </h1>
          </div>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">Créer votre espace salon</p>
        </div>

        <form onSubmit={handleSubmit} noValidate style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Pays */}
          <div>
            <label style={{ display:'block', fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:8 }}>
              Pays <span style={{ color:'#e11d48' }}>*</span>
            </label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {PAYS_OPTIONS.map(p => (
                <button key={p.value} type="button" onClick={() => set('pays', p.value)}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center', gap:6,
                    padding:'14px 12px', borderRadius:16, border:'2px solid',
                    borderColor: form.pays === p.value ? '#3b82f6' : '#f1f5f9',
                    background:  form.pays === p.value ? '#eff6ff' : '#f8fafc',
                    color:       form.pays === p.value ? '#1d4ed8' : '#64748b',
                    fontWeight:700, fontSize:13, cursor:'pointer', transition:'all 0.18s',
                    boxShadow: form.pays === p.value ? '0 0 0 3px rgba(59,130,246,0.1)' : 'none',
                  }}>
                  <span style={{ fontSize:22 }}>{p.flag}</span>
                  <span>{p.value}</span>
                </button>
              ))}
            </div>
            {errors.pays && <p style={{ color:'#e11d48', fontSize:11, fontWeight:700, marginTop:4 }}>{errors.pays}</p>}
          </div>

          {paysInfo && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:14, padding:'10px 16px' }}>
              <span style={{ color:'#64748b', fontSize:12, fontWeight:700 }}>Abonnement mensuel</span>
              <span style={{ color:'#1d4ed8', fontSize:14, fontWeight:900 }}>{paysInfo.prix}</span>
            </div>
          )}

          <Section label="Votre salon">
            <Field label="Nom du salon" error={errors.nomSalon}>
              <input type="text" className={`ss-input${errors.nomSalon ? ' err' : ''}`} value={form.nomSalon} onChange={e => set('nomSalon', e.target.value)} placeholder={ph.nomSalon} />
            </Field>
            <Field label="Adresse" error={errors.adresse}>
              <input type="text" className={`ss-input${errors.adresse ? ' err' : ''}`} value={form.adresse} onChange={e => set('adresse', e.target.value)} placeholder={ph.adresse} />
            </Field>
          </Section>

          <Section label="Vos informations">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label="Prénom" error={errors.prenomAdmin}>
                <input type="text" className={`ss-input${errors.prenomAdmin ? ' err' : ''}`} value={form.prenomAdmin} onChange={e => set('prenomAdmin', e.target.value)} placeholder={ph.prenom} />
              </Field>
              <Field label="Nom" error={errors.nomAdmin}>
                <input type="text" className={`ss-input${errors.nomAdmin ? ' err' : ''}`} value={form.nomAdmin} onChange={e => set('nomAdmin', e.target.value)} placeholder={ph.nom} />
              </Field>
            </div>
            <Field label="Email" error={errors.emailAdmin}>
              <input type="email" className={`ss-input${errors.emailAdmin ? ' err' : ''}`} value={form.emailAdmin} onChange={e => set('emailAdmin', e.target.value)} placeholder={ph.email} />
            </Field>
            <Field label="Téléphone" error={errors.telephone}>
              <input type="tel" className={`ss-input${errors.telephone ? ' err' : ''}`} value={form.telephone} onChange={e => set('telephone', filterPhone(e.target.value))} placeholder={ph.telephone} />
            </Field>
          </Section>

          <Section label="Mot de passe">
            <Field label="Mot de passe" error={errors.password}>
              <input type="password" className={`ss-input${errors.password ? ' err' : ''}`} value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" />
            </Field>
            <Field label="Confirmer" error={errors.confirmPassword}>
              <input type="password" className={`ss-input${errors.confirmPassword ? ' err' : ''}`} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••" />
            </Field>
          </Section>

          {serverError && (
            <div style={{ background:'#fff1f2', color:'#e11d48', padding:'12px 16px', borderRadius:14, fontSize:12, fontWeight:700, border:'1px solid #fecdd3', display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:'#e11d48', flexShrink:0 }} />
              {serverError}
            </div>
          )}

          <button type="submit" disabled={loading} className="ss-btn"
            style={{ width:'100%', background:'linear-gradient(135deg,#2563eb,#3b82f6)', color:'#fff', fontWeight:900, fontSize:15, padding:'15px', borderRadius:18, border:'none', cursor:'pointer', boxShadow:'0 12px 32px rgba(59,130,246,0.35)', transition:'all 0.25s', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
          >
            {loading ? <><Loader2 size={19} className="animate-spin" /> Envoi en cours…</> : 'Envoyer ma demande'}
          </button>
        </form>

        <div style={{ marginTop:20, paddingTop:18, borderTop:'1px solid #f1f5f9', textAlign:'center' }}>
          <p style={{ color:'#64748b', fontSize:13, fontWeight:500, marginBottom:6 }}>
            Déjà un compte ?{' '}
            <Link to="/login" style={{ color:'#2563eb', fontWeight:800, textDecoration:'none' }}>Se connecter</Link>
          </p>
          <p style={{ color:'#cbd5e1', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.12em' }}>
            &copy; 2026 SamaSalon
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <div style={{ height:1, width:20, background:'linear-gradient(90deg, #3b82f6, transparent)' }} />
        <span style={{ fontSize:10, fontWeight:800, color:'#3b82f6', textTransform:'uppercase', letterSpacing:'0.12em', whiteSpace:'nowrap' }}>{label}</span>
        <div style={{ flex:1, height:1, background:'linear-gradient(90deg, rgba(59,130,246,0.2), transparent)' }} />
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>{children}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>{label}</label>
      {children}
      {error && <p style={{ color:'#e11d48', fontSize:11, fontWeight:700, marginTop:4 }}>{error}</p>}
    </div>
  );
}
